import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft,
  Search,
  X,
  Loader2,
  Link as LinkIcon,
  Image as ImageIcon,
  FileText,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useContacts } from '@/hooks/use-contacts'
import { useCreateContactNote } from '@/hooks/use-contact-notes'
import { getSharedContent, clearSharedContent, type SharedContent } from '@/lib/share-receiver'
import type { Contact } from '@/lib/types'

function getInitials(firstName: string, lastName: string | null): string {
  return firstName.charAt(0).toUpperCase() + (lastName?.charAt(0).toUpperCase() ?? '')
}

function getContactName(c: { firstName: string; lastName: string | null }): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ')
}

function isUrl(text: string): boolean {
  try {
    new URL(text.trim())
    return true
  } catch {
    return false
  }
}

export default function QuickCapturePage() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [sharedContent, setSharedContent] = useState<SharedContent | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedContact, setSelectedContact] = useState<{
    id: string
    firstName: string
    lastName: string | null
    avatarUrl: string | null
  } | null>(null)
  const [contactSearch, setContactSearch] = useState('')
  const [additionalNote, setAdditionalNote] = useState('')

  const createNote = useCreateContactNote()
  const { data: contactResults } = useContacts({ q: contactSearch, limit: 10 })

  // Load shared content on mount
  useEffect(() => {
    getSharedContent()
      .then((content) => {
        setSharedContent(content)
        setLoading(false)
      })
      .catch(() => {
        setSharedContent({ type: 'none' })
        setLoading(false)
      })
  }, [])

  // Filter contact results to exclude the selected contact
  const filteredContacts = useMemo(() => {
    if (!contactResults?.items) return []
    if (!selectedContact) return contactResults.items
    return contactResults.items.filter((c) => c.id !== selectedContact.id)
  }, [contactResults, selectedContact])

  function selectContact(c: Contact) {
    setSelectedContact({
      id: c.id,
      firstName: c.firstName,
      lastName: c.lastName,
      avatarUrl: c.avatarUrl,
    })
    setContactSearch('')
  }

  async function handleSave() {
    if (!selectedContact || !sharedContent || sharedContent.type === 'none') return

    let noteContent = ''

    if (sharedContent.type === 'text') {
      const text = sharedContent.text || ''
      const subject = sharedContent.subject || ''

      if (isUrl(text)) {
        noteContent = subject ? `[${subject}](${text.trim()})` : text.trim()
      } else {
        noteContent = subject ? `**${subject}**\n\n${text}` : text
      }
    } else if (sharedContent.type === 'image') {
      noteContent = t('quickCapture.imageShared')
    }

    if (additionalNote.trim()) {
      noteContent = noteContent
        ? `${noteContent}\n\n---\n${additionalNote.trim()}`
        : additionalNote.trim()
    }

    if (!noteContent) return

    createNote.mutate(
      { contactId: selectedContact.id, content: noteContent },
      {
        onSuccess: async () => {
          await clearSharedContent()
          navigate(`/contacts/${selectedContact.id}`)
        },
      },
    )
  }

  function handleClose() {
    clearSharedContent()
    navigate(-1)
  }

  if (loading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Loader2 className="text-primary size-8 animate-spin" />
      </div>
    )
  }

  if (!sharedContent || sharedContent.type === 'none') {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 px-6">
        <p className="text-on-surface-variant text-center">{t('quickCapture.noContent')}</p>
        <button onClick={() => navigate(-1)} className="text-primary text-sm font-medium">
          {t('common.cancel')}
        </button>
      </div>
    )
  }

  const canSave = !!selectedContact && !createNote.isPending

  return (
    <div className="bg-background flex h-dvh flex-col">
      {/* Header */}
      <header className="border-outline-variant flex items-center gap-3 border-b px-4 py-3">
        <button onClick={handleClose} className="text-on-surface -ml-1 p-1">
          <ChevronLeft className="size-6" />
        </button>
        <h1 className="text-on-surface flex-1 text-lg font-semibold">{t('quickCapture.title')}</h1>
        <button
          onClick={handleSave}
          disabled={!canSave}
          className={cn(
            'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
            canSave
              ? 'bg-primary text-on-primary'
              : 'bg-surface-container-high text-on-surface-variant cursor-not-allowed opacity-50',
          )}
        >
          {createNote.isPending ? <Loader2 className="size-4 animate-spin" /> : t('common.save')}
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Shared content preview */}
        <section className="mb-6">
          <label className="text-on-surface-variant mb-2 block text-xs font-medium tracking-wide uppercase">
            {t('quickCapture.sharedContent')}
          </label>
          <div className="border-outline-variant bg-surface-container-low rounded-xl border p-4">
            {sharedContent.type === 'text' && (
              <div className="flex gap-3">
                <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-lg">
                  {isUrl(sharedContent.text || '') ? (
                    <LinkIcon className="text-primary size-5" />
                  ) : (
                    <FileText className="text-primary size-5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  {sharedContent.subject && (
                    <p className="text-on-surface mb-1 text-sm font-medium">
                      {sharedContent.subject}
                    </p>
                  )}
                  <p className="text-on-surface-variant text-sm break-words">
                    {(sharedContent.text || '').length > 300
                      ? (sharedContent.text || '').slice(0, 300) + '...'
                      : sharedContent.text}
                  </p>
                </div>
              </div>
            )}
            {sharedContent.type === 'image' && (
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-lg">
                  <ImageIcon className="text-primary size-5" />
                </div>
                <p className="text-on-surface text-sm">{t('quickCapture.imageAttached')}</p>
              </div>
            )}
          </div>
        </section>

        {/* Contact picker */}
        <section className="mb-6">
          <label className="text-on-surface-variant mb-2 block text-xs font-medium tracking-wide uppercase">
            {t('quickCapture.selectContact')}
          </label>

          {selectedContact && (
            <div className="border-outline-variant mb-3 flex items-center gap-3 rounded-xl border p-3">
              {selectedContact.avatarUrl ? (
                <img
                  src={selectedContact.avatarUrl}
                  alt=""
                  className="size-10 rounded-full object-cover"
                />
              ) : (
                <div className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-full text-sm font-medium">
                  {getInitials(selectedContact.firstName, selectedContact.lastName)}
                </div>
              )}
              <span className="text-on-surface flex-1 text-sm font-medium">
                {getContactName(selectedContact)}
              </span>
              <button
                onClick={() => setSelectedContact(null)}
                className="text-on-surface-variant p-1"
              >
                <X className="size-4" />
              </button>
            </div>
          )}

          {!selectedContact && (
            <>
              <div className="border-outline-variant relative mb-2 flex items-center gap-2 rounded-xl border px-3 py-2.5">
                <Search className="text-on-surface-variant size-4 shrink-0" />
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder={t('quickCapture.searchContacts')}
                  className="text-on-surface placeholder:text-on-surface-variant w-full bg-transparent text-sm outline-none"
                  autoFocus
                />
                {contactSearch && (
                  <button
                    onClick={() => setContactSearch('')}
                    className="text-on-surface-variant p-1"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>

              {filteredContacts.length > 0 && (
                <div className="border-outline-variant divide-outline-variant max-h-60 divide-y overflow-y-auto rounded-xl border">
                  {filteredContacts.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => selectContact(c)}
                      className="hover:bg-surface-container-low flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors"
                    >
                      {c.avatarUrl ? (
                        <img
                          src={c.avatarUrl}
                          alt=""
                          className="size-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="bg-primary/10 text-primary flex size-8 items-center justify-center rounded-full text-xs font-medium">
                          {getInitials(c.firstName, c.lastName)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-on-surface truncate text-sm font-medium">
                          {getContactName(c)}
                        </p>
                        {c.company && (
                          <p className="text-on-surface-variant truncate text-xs">{c.company}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {contactSearch && filteredContacts.length === 0 && (
                <p className="text-on-surface-variant py-4 text-center text-sm">
                  {t('quickCapture.noContactsFound')}
                </p>
              )}
            </>
          )}
        </section>

        {/* Additional note */}
        <section>
          <label className="text-on-surface-variant mb-2 block text-xs font-medium tracking-wide uppercase">
            {t('quickCapture.addNote')}
          </label>
          <textarea
            value={additionalNote}
            onChange={(e) => setAdditionalNote(e.target.value)}
            placeholder={t('quickCapture.notePlaceholder')}
            rows={3}
            className="border-outline-variant bg-surface-container-low text-on-surface placeholder:text-on-surface-variant focus:border-primary w-full rounded-xl border px-4 py-3 text-sm outline-none"
          />
        </section>
      </div>
    </div>
  )
}
